import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';

import { ResetPasswordForm } from '../../organisms';
import i18n from '@/shared/i18n';
import {
  useAddPassword,
  useInitPasswordCreation,
  useRegisterInvitation,
} from '@/features/auth/hooks';
import { useCreatePasswordStore } from '@/features/auth/store/createPassword';
import { clearOnboardingToken, setOnboardingToken } from '@/shared/api/storage';
import { CreatePasswordError } from '../../molecules/CreatePasswordError';
import { CreatePasswordLoading } from '../../molecules/CreatePasswordLoading';
import {
  hasApiErrorCode,
  getFederatedLoginRequired,
} from '@/shared/utils/apiError';
import {
  PASSWORD_ERROR_CODES,
  PASSWORD_ALREADY_USED_MESSAGE_KEY,
} from '@/features/auth/utils/passwordErrorCodes';
import type { FederatedLoginLocationState } from '@/features/auth/types/auth.types';

/**
 * CreatePasswordPage
 *
 * Handles password creation on two entry paths:
 *
 * **Invite flow** (new user via invitation link):
 * - Route: /create-password?inviteId=&token=&existingUser=false
 * - Calls registerInvitation to obtain the onboarding token, then addPassword
 *   with it.
 *
 * **First-access flow** (Business Account owner via onboarding email):
 * - Route: /create-password (no query params)
 * - Requires router state `{ firstAccess: true }`, set by JoinPage after
 *   `/auth/identify` succeeded (which kept the onboarding token).
 * - Skips registerInvitation and calls addPassword directly.
 *
 * The onboarding token is kept in memory only (never in router state or the
 * login session) and is discarded once the password step ends.
 */
const CreatePasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const isFirstAccess =
    (state as { firstAccess?: boolean } | null)?.firstAccess === true;

  const {
    currentStep,
    error,
    isInvitationVerified,
    invitationAccepted,
    invitationFailed,
    passwordCreated,
    passwordFailed,
    reset,
  } = useCreatePasswordStore();

  const inviteId = searchParams.get('inviteId');
  const token = searchParams.get('token');
  const existingUser = searchParams.get('existingUser');

  const { mutate: registerInvitationMutate, isPending: isRegisterPending } =
    useRegisterInvitation({
      onSuccess: (data) => {
        if (data?.success === false || !data?.accessToken) {
          invitationFailed(i18n.t('Failed to accept invitation'));
          return;
        }

        setOnboardingToken(data.accessToken);
        invitationAccepted();
      },
      onError: (err) => {
        invitationFailed(err.message || i18n.t('Failed to accept invitation'));
      },
    });

  const { mutate: addPasswordMutate, isPending: isAddPasswordPending } =
    useAddPassword({
      onSuccess: () => {
        // The onboarding token only authorizes this step: the user signs in
        // with the new password.
        clearOnboardingToken();
        passwordCreated();
      },
      onError: (err) => {
        // CA07 — convidado de cliente federado não define senha própria: o
        // perfil e o vínculo já foram provisionados pelo convite; o acesso
        // ocorre pelo fluxo corporativo.
        const federated = getFederatedLoginRequired(err);
        if (federated) {
          clearOnboardingToken();
          const ssoState: FederatedLoginLocationState = {
            reason: 'federated-required',
            registrationId: federated.registrationId,
            authenticationUrl: federated.authenticationUrl,
          };
          navigate('/sso', { state: ssoState, replace: true });
          return;
        }
        if (hasApiErrorCode(err, PASSWORD_ERROR_CODES.ALREADY_EXISTS)) {
          passwordFailed(
            i18n.t('User already has a registered password. Please sign in.')
          );
          return;
        }
        if (hasApiErrorCode(err, PASSWORD_ERROR_CODES.ALREADY_USED)) {
          passwordFailed(i18n.t(PASSWORD_ALREADY_USED_MESSAGE_KEY));
          return;
        }
        passwordFailed(err.message || i18n.t('Failed to set password'));
      },
    });

  useInitPasswordCreation({
    isFirstAccess,
    inviteId,
    token,
    existingUser,
    invitationAccepted,
    invitationFailed,
    reset,
    registerInvitation: registerInvitationMutate,
  });

  const handleCreatePassword = async (data: { newPassword: string }) => {
    if (!isInvitationVerified) {
      passwordFailed(i18n.t('Invitation not validated yet. Please try again.'));
      return;
    }

    addPasswordMutate({ password: data.newPassword });
  };

  // Error state - check first to show errors immediately
  if (currentStep === 'error') {
    return <CreatePasswordError />;
  }

  // Loading state
  if (
    currentStep === 'loading' ||
    (isRegisterPending && !isInvitationVerified)
  ) {
    return <CreatePasswordLoading />;
  }

  return (
    <ResetPasswordForm
      onSubmit={handleCreatePassword}
      loading={isAddPasswordPending}
      error={error}
      success={currentStep === 'success'}
    />
  );
};

export default CreatePasswordPage;
